import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

export default function Hero() {
  const { t } = useTranslation();

  const badges = [
    { icon: 'memory', text: t('hero.badges.saas') },
    { icon: 'code', text: t('hero.badges.tech') },
    { icon: 'verified_user', text: t('hero.badges.experience') },
  ];

  return (
    <main className="relative flex min-h-screen items-center overflow-hidden pb-28 pt-32 md:pb-32" id="main-content">
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-purple-primary/20 rounded-full blur-[120px] -z-10 pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-accent-magenta/10 rounded-full blur-[100px] -z-10 pointer-events-none" />
      <motion.img src="/logo-icon.png" alt="" aria-hidden="true" className="absolute -bottom-32 -right-32 w-[500px] h-[500px] object-contain opacity-[0.04] rotate-[-15deg] pointer-events-none select-none" animate={{ scale: [1, 1.08, 1], opacity: [0.04, 0.07, 0.04] }} transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }} />

      <div className="mx-auto grid w-full min-w-0 max-w-7xl grid-cols-1 items-center gap-12 px-4 sm:px-6 lg:grid-cols-2">
        <motion.div className="min-w-0 space-y-8" initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}>

          <motion.div className="inline-flex items-center px-4 py-1.5 rounded-full border border-purple-primary bg-purple-primary/10" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.3, duration: 0.5 }}>
            <span className="text-purple-primary text-xs font-bold tracking-widest uppercase font-label">
              {t('hero.badge')}
            </span>
          </motion.div>

          <motion.h1 className="break-words text-4xl sm:text-5xl font-black font-headline tracking-tighter leading-[1.1] md:text-7xl" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 0.7 }}>
            {t('hero.title')} <br />
            <span className="gradient-text">{t('hero.titleHighlight')}</span>
          </motion.h1>

          <motion.p className="text-on-surface-variant text-base sm:text-lg md:text-xl leading-relaxed max-w-xl font-body" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4, duration: 0.7 }}>
            {t('hero.subtitle')}
          </motion.p>

          <motion.div className="flex flex-wrap gap-4 pt-4" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6, duration: 0.7 }}>
            <motion.a href="https://compliance.crosspaysolutions.app/" className="flex items-center gap-2 px-5 sm:px-8 py-3.5 sm:py-4 bg-gradient-to-r from-purple-primary to-accent-magenta text-white rounded-xl font-bold font-headline shadow-lg shadow-purple-primary/20 relative overflow-hidden group" target="_blank" rel="noopener noreferrer" whileHover={{ scale: 1.05, boxShadow: "0 0 30px rgba(168,67,151,0.4)" }} whileTap={{ scale: 0.97 }} transition={{ type: "spring", stiffness: 400, damping: 17 }}>
              <span className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              <span className="relative">{t('hero.ctaPrimary')}</span>
              <motion.span className="material-symbols-outlined relative" animate={{ x: [0, 4, 0] }} transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}>arrow_forward</motion.span>
            </motion.a>
            <motion.a href="/contact" className="px-5 sm:px-8 py-3.5 sm:py-4 border-2 border-purple-primary text-purple-primary rounded-xl font-bold font-headline relative overflow-hidden group" whileHover={{ scale: 1.05, borderColor: "#D23CB3" }} whileTap={{ scale: 0.97 }}>
              <span className="absolute inset-0 bg-purple-primary/0 group-hover:bg-purple-primary/10 transition-all duration-300" />
              <span className="relative">{t('hero.ctaSecondary')}</span>
            </motion.a>
          </motion.div>

          <motion.div className="flex flex-wrap items-center gap-6 pt-6" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8, duration: 0.7 }}>
            {badges.map((badge, i) => (
              <motion.div key={badge.text} className="flex items-center gap-2 text-on-surface-variant/80" initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.9 + i * 0.15, duration: 0.5 }}>
                <span className="material-symbols-outlined text-purple-primary text-[20px]">{badge.icon}</span>
                <span className="text-sm font-medium">{badge.text}</span>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>

        {/* Dashboard Mockup */}
        {/* Mobile dashboard image */}
        <motion.div
          className="relative mt-8 min-w-0 lg:hidden"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.6 }}
        >
          <div className="absolute -inset-2 bg-gradient-to-r from-purple-primary to-accent-magenta rounded-2xl blur-lg opacity-15" />
          <img
            src="/dashboard-preview.png"
            alt={t('hero.dashboardAlt')}
            className="relative w-full h-auto rounded-xl border border-outline-variant/20 shadow-xl"
          />
        </motion.div>

        {/* Desktop dashboard */}
        <motion.div
          className="group relative hidden min-w-0 lg:block"
          initial={{ opacity: 0, x: 60, rotateY: -5 }}
          animate={{ opacity: 1, x: 0, rotateY: 0 }}
          transition={{ duration: 1, delay: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
        >
          <motion.div
            className="absolute -inset-3 bg-gradient-to-r from-purple-primary to-accent-magenta rounded-2xl blur-xl opacity-20"
            animate={{ opacity: [0.1, 0.25, 0.1] }}
            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div
            className="relative rounded-2xl overflow-hidden shadow-2xl border border-outline-variant/20"
            whileHover={{ y: -6, boxShadow: "0 20px 60px rgba(168,67,151,0.2)" }}
            transition={{ type: "spring", stiffness: 200, damping: 20 }}
          >
            <img
              src="/dashboard-preview.png"
              alt={t('hero.dashboardAltFull')}
              className="w-full h-auto rounded-2xl"
            />
          </motion.div>
        </motion.div>
      </div>
    </main>
  );
}
